import * as THREE from 'three';

import { AmbientLightComponent } from '../../components/lights/AmbientLightComponent.js';
import { DirectionalLightComponent } from '../../components/lights/DirectionalLightComponent.js';
import { HemisphereLightComponent } from '../../components/lights/HemisphereLightComponent.js';
import { PointLightComponent } from '../../components/lights/PointLightComponent.js';
import { RectAreaLightComponent } from '../../components/lights/RectAreaLightComponent.js';
import { SpotLightComponent } from '../../components/lights/SpotLightComponent.js';
import { MeshComponent } from '../../components/visual/MeshComponent.js';
import { ModelMeshComponent } from '../../components/visual/ModelMeshComponent.js';


import { glbSourceIndexedFromBuffer } from './glbSourceIndexed.js';
import {
  LIGHTMAP_SCENE_EXPORT_COORDINATE_SYSTEM,
  threeMatrixWorldToBlenderArray,
  threeToBlenderDirectionArray,
} from './lightmapBlenderCoordinates.js';
import { createLightmapLightId, createLightmapTargetId } from './lightmapObjectId.js';
import {
  extractLightmapMeshGeometryBlender,
  hasExportableGeometry,
  objectBaseFields,
  worldTransformForComponent,
} from './lightmapSceneExportMeshHelpers.js';
import { LIGHTMAP_SCENE_EXPORT_VERSION } from './lightmapSceneExportTypes.js';
import { snapshotLightmapMaterial } from './snapshotLightmapMaterial.js';

import type {
  LightmapSceneExport,
  LightmapSceneExportLight,
  LightmapSceneExportLightType,
  LightmapSceneExportMeshObject,
  LightmapSceneExportModelObject,
  LightmapSceneExportObject,
} from './lightmapSceneExportTypes.js';
import type { Actor } from '../../actors/Actor.js';
import type { SceneComponent } from '../../components/SceneComponent.js';
import type { World } from '../../game/World.js';

export type CollectLightmapSceneExportOptions = {
  readModelBytes?: (modelUrl: string) => Promise<ArrayBuffer>;
  excludeActor?: Actor;
};

const _lightDirection = new THREE.Vector3();

async function exportMeshObject(
  actor: Actor,
  component: MeshComponent,
): Promise<LightmapSceneExportMeshObject | null> {
  const mesh = component.mesh;
  const lightmapTargetId = createLightmapTargetId(component.uuid);
  if (!hasExportableGeometry(mesh.geometry)) {
    console.warn(`[lightmapper] Skipping ${lightmapTargetId}: mesh has no position vertices`);
    return null;
  }

  const material = snapshotLightmapMaterial(mesh.material);

  return {
    type: 'mesh',
    ...objectBaseFields(actor, component, 0, lightmapTargetId),
    ...worldTransformForComponent(component),
    ...extractLightmapMeshGeometryBlender(mesh.geometry),
    ...(material ? { material } : {}),
  };
}

async function exportModelObject(
  actor: Actor,
  component: ModelMeshComponent,
  options: CollectLightmapSceneExportOptions,
  sourceIndexedCache: Map<string, boolean>,
): Promise<LightmapSceneExportModelObject | null> {
  const modelUrl = component.modelUrl;
  const lightmapTargetId = createLightmapTargetId(component.uuid);
  if (!modelUrl) {
    console.warn(`[lightmapper] Skipping ${lightmapTargetId}: ModelMeshComponent has no modelUrl`);
    return null;
  }
  if (!options.readModelBytes) {
    console.warn(`[lightmapper] Skipping ${lightmapTargetId}: readModelBytes is required for model export`);
    return null;
  }

  await component.waitForLoad();

  let sourceIndexed = sourceIndexedCache.get(modelUrl);
  if (sourceIndexed === undefined) {
    try {
      sourceIndexed = glbSourceIndexedFromBuffer(await options.readModelBytes(modelUrl));
      sourceIndexedCache.set(modelUrl, sourceIndexed);
    } catch (err) {
      console.warn(
        `[lightmapper] Skipping ${lightmapTargetId}: failed to inspect GLB indices`,
        err instanceof Error ? err.message : err,
      );
      return null;
    }
  }

  const firstMesh = component.getAllMeshes()[0];
  return {
    type: 'model',
    ...objectBaseFields(actor, component, 0, lightmapTargetId),
    ...worldTransformForComponent(component),
    modelUrl,
    sourceIndexed,
    ...(firstMesh ? { material: snapshotLightmapMaterial(firstMesh.material) ?? undefined } : {}),
  };
}

function getThreeLight(component: SceneComponent): THREE.Light | null {
  let light: THREE.Light | null = null;
  component.traverse(child => {
    if (!light && child instanceof THREE.Light) {
      light = child;
    }
  });
  return light;
}

function serializeLight(
  actor: Actor,
  component: SceneComponent,
  light: THREE.Light,
  type: LightmapSceneExportLightType,
  extra: Partial<LightmapSceneExportLight> = {},
): LightmapSceneExportLight {
  component.updateMatrixWorld(true);
  const actorName = actor.name || actor.uuid;
  component.getWorldDirection(_lightDirection);
  return {
    exportLightId: createLightmapLightId(actor.uuid, component.uuid),
    type,
    name: `${actorName}:${component.constructor.name}`,
    color: [light.color.r, light.color.g, light.color.b],
    intensity: light.intensity,
    matrixWorldBlender: threeMatrixWorldToBlenderArray(component.matrixWorld),
    directionBlender: threeToBlenderDirectionArray(_lightDirection),
    castShadow: 'castShadow' in light ? !!(light as THREE.Light & { castShadow?: boolean }).castShadow : false,
    ...extra,
  };
}

function collectLights(world: World, excludeActor?: Actor): LightmapSceneExportLight[] {
  const lights: LightmapSceneExportLight[] = [];

  for (const actor of world.getActors()) {
    if (actor === excludeActor) {
      continue;
    }

    for (const component of actor.getComponents(DirectionalLightComponent)) {
      const light = getThreeLight(component);
      if (light) {
        lights.push(serializeLight(actor, component, light, 'directional'));
      }
    }
    for (const component of actor.getComponents(PointLightComponent)) {
      const light = getThreeLight(component) as THREE.PointLight | null;
      if (light) {
        lights.push(serializeLight(actor, component, light, 'point', {
          distance: light.distance,
          decay: light.decay,
        }));
      }
    }
    for (const component of actor.getComponents(SpotLightComponent)) {
      const light = getThreeLight(component) as THREE.SpotLight | null;
      if (light) {
        lights.push(serializeLight(actor, component, light, 'spot', {
          distance: light.distance,
          decay: light.decay,
          angle: light.angle,
          penumbra: light.penumbra,
        }));
      }
    }
    for (const component of actor.getComponents(HemisphereLightComponent)) {
      const light = getThreeLight(component) as THREE.HemisphereLight | null;
      if (light) {
        lights.push(serializeLight(actor, component, light, 'hemisphere', {
          groundColor: [light.groundColor.r, light.groundColor.g, light.groundColor.b],
        }));
      }
    }
    for (const component of actor.getComponents(RectAreaLightComponent)) {
      const light = getThreeLight(component) as THREE.RectAreaLight | null;
      if (light) {
        lights.push(serializeLight(actor, component, light, 'rectArea', {
          width: light.width,
          height: light.height,
        }));
      }
    }
    for (const component of actor.getComponents(AmbientLightComponent)) {
      const light = getThreeLight(component);
      if (light) {
        lights.push(serializeLight(actor, component, light, 'ambient'));
      }
    }
  }

  return lights;
}

export async function collectLightmapSceneExport(
  world: World,
  runId: string,
  options: CollectLightmapSceneExportOptions = {},
): Promise<LightmapSceneExport> {
  const objects: LightmapSceneExportObject[] = [];
  const sourceIndexedCache = new Map<string, boolean>();

  for (const actor of world.getActors()) {
    if (actor === options.excludeActor) {
      continue;
    }

    for (const component of actor.getComponents(MeshComponent)) {
      if (component.isEditorOnly()) {
        continue;
      }
      const object = await exportMeshObject(actor, component);
      if (object) {
        objects.push(object);
      }
    }

    for (const component of actor.getComponents(ModelMeshComponent)) {
      if (component.isEditorOnly()) {
        continue;
      }
      const object = await exportModelObject(actor, component, options, sourceIndexedCache);
      if (object) {
        objects.push(object);
      }
    }
  }

  return {
    version: LIGHTMAP_SCENE_EXPORT_VERSION,
    coordinateSystem: LIGHTMAP_SCENE_EXPORT_COORDINATE_SYSTEM,
    runId,
    objects,
    lights: collectLights(world, options.excludeActor),
  };
}

export const collectGenesysLightmapperSceneExport = collectLightmapSceneExport;
